import { useRef } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { Users, Stethoscope, Briefcase, Desktop, WhatsappLogo } from "@phosphor-icons/react";
import { trackLeaseIntentClick } from "@/lib/gtag";
import { useMedia } from "@/context/MediaContext";
import { resolveImg } from "@/lib/api";

const FEATURES = [
  { icon: Briefcase, title: "2 Salas Executivas", text: "Ambientes premium e exclusivos para o seu negócio." },
  { icon: Stethoscope, title: "1 Consultório", text: "Estrutura ideal para profissionais da saúde." },
  { icon: Users, title: "Sala de Reuniões", text: "Espaço equipado para até 8 pessoas." },
  { icon: Desktop, title: "Coworking", text: "8 posições de trabalho em ambiente compartilhado." },
];

const MODALIDADES = ["Por Hora", "Escritório Virtual", "Ponto Comercial e Fiscal"];

export default function VMCorporate() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const { corporate_feature_image, corporate_intro_text } = useMedia();

  return (
    <section id="corporate" ref={ref} className="relative bg-vm-surface py-24 md:py-32 overflow-hidden" data-testid="corporate-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <div>
          <motion.p initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ duration: 0.6 }} className="text-xs tracking-[0.4em] uppercase text-vm-gold mb-6">
            Salas & Escritórios
          </motion.p>
          <motion.h2 initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }} className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]" data-testid="corporate-title">
            VM <span className="gold-text italic">Corporate</span>
          </motion.h2>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.15 }} className="mt-6 text-lg text-vm-mutedFg max-w-lg leading-relaxed" data-testid="corporate-intro-text">
            {corporate_intro_text}
          </motion.p>

          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.2 + i * 0.1 }}
                className="group rounded-xl border border-vm-border bg-vm-muted p-5 hover:border-vm-gold/40 transition-colors duration-500 gold-glow"
                data-testid={`corporate-feature-${i}`}
              >
                <f.icon size={26} weight="duotone" className="text-vm-gold mb-4" />
                <h3 className="text-base font-medium mb-1.5">{f.title}</h3>
                <p className="text-sm text-vm-mutedFg leading-relaxed">{f.text}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-8"
            data-testid="corporate-modalidades"
          >
            <span className="text-xs tracking-[0.25em] uppercase text-vm-mutedFg">Modalidades</span>
            <div className="mt-3 flex flex-wrap gap-2.5">
              {MODALIDADES.map((m) => (
                <span key={m} className="rounded-full border border-vm-gold/30 bg-vm-gold/10 px-4 py-1.5 text-sm text-vm-gold">
                  {m}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.a
            href={`https://wa.me/5521987356892?text=${encodeURIComponent("Olá! Gostaria de mais informações sobre as salas e escritórios do VM Corporate.")}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackLeaseIntentClick()}
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="btn-sweep relative z-0 mt-10 inline-flex items-center gap-2 bg-vm-gold text-vm-bg hover:text-vm-bg font-semibold px-7 py-4 rounded-full"
            data-testid="corporate-cta-reserve"
          >
            Reserve Já pelo WhatsApp
            <WhatsappLogo size={18} weight="fill" />
          </motion.a>
        </div>

        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={inView ? { opacity: 1, scale: 1 } : {}} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }} className="relative">
          <div className="relative overflow-hidden rounded-2xl border border-vm-border aspect-[4/5]">
            <motion.img style={{ y: imgY }} src={resolveImg(corporate_feature_image)} alt="Sala executiva premium do VM Corporate" className="w-full h-[115%] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-vm-bg/60 to-transparent" />
          </div>
          <div className="absolute -bottom-5 -left-5 bg-vm-bg border border-vm-gold/40 rounded-xl px-6 py-4">
            <span className="font-serif text-3xl gold-text">100%</span>
            <span className="block text-xs tracking-widest uppercase text-vm-mutedFg">Foco em Negócios</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
